import React, { useCallback, useEffect, useState } from 'react';
import { proService } from '../../services';
import { useUser } from '../../context/UserContext';
import { useDialog } from '../../context/DialogContext';
import { AdminPageHeader, EmptyLine, Loading, Pill, money, useAdminShell } from '../../components/admin/shell';

const reasonLabel = (r) => (r || '').replace(/_/g, ' ');
const when = (d) => {
  if (!d) return '—';
  const x = new Date(d);
  return Number.isNaN(x.getTime()) ? '—' : x.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const STRIKES = [{ key: null, label: 'NONE' }, { key: 'owner', label: 'OWNER' }, { key: 'buyer', label: 'BUYER' }];
const chip = 'h-[34px] rounded-full px-[14px] font-mono text-[10px] font-bold uppercase tracking-[0.12em] transition-colors duration-200';
const resolveBtn = 'h-11 rounded-full px-4 text-center font-mono text-[11px] font-bold uppercase tracking-[0.12em] transition-colors duration-200 disabled:opacity-50';

/**
 * MONEY › DISPUTES — spec/sections.md §8. One card per open Pro booking flag.
 * The resolve block (strike + split + the four resolutions) exists only for
 * `disputes` · resolve; everyone else with `view` reads the cards.
 */
export default function DisputesPage() {
  const { can } = useUser();
  const { showConfirm, showAlert } = useDialog();
  const { refreshCounts } = useAdminShell();
  const canResolve = can('disputes', 'resolve');
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [splitById, setSplitById] = useState({});
  const [strikeById, setStrikeById] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    const r = await proService.openFlags();
    if (r.success) setFlags(r.data || []);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const resolve = async (flag, resolution) => {
    if (resolution === 'refunded') {
      const ok = await showConfirm({ title: 'REFUND BUYER', message: `Refund ${money(flag.amountCents)} to the buyer and close this flag.`, confirmLabel: 'REFUND', variant: 'primary', theme: 'admin' });
      if (!ok) return;
    }
    setBusyId(flag.id);
    const body = { resolution, strikeSide: strikeById[flag.id] || null };
    if (resolution === 'split') body.splitToBuyerCents = Math.round(parseFloat(splitById[flag.id] || '0') * 100);
    const r = await proService.resolveFlag(flag.id, body);
    setBusyId(null);
    if (r.success) { await load(); refreshCounts(); }
    else await showAlert({ title: 'COULD NOT RESOLVE', message: r.error || 'Try again.', type: 'error', theme: 'admin' });
  };

  return (
    <>
      <AdminPageHeader section="disputes" meta="Pro booking flags. Resolving one moves money out of escrow." />
      <div className="flex max-w-[760px] flex-col gap-[14px]">
        {loading && <div className="rounded-[24px] bg-white p-6"><Loading /></div>}
        {!loading && flags.length === 0 && <div className="rounded-[24px] bg-white p-3"><EmptyLine>NO OPEN DISPUTES.</EmptyLine></div>}
        {!loading && flags.map((f) => {
          const escalated = (f.ladderTier || 0) >= 1;
          const strike = strikeById[f.id] || null;
          const busy = busyId === f.id;
          return (
            <article key={f.id} className="rounded-[24px] bg-white p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-[13px] font-bold uppercase tracking-[0.05em]">{f.businessName || '—'}</span>
                    <Pill tone={escalated ? 'bad' : 'neutral'}>{escalated ? 'ESCALATED' : 'AWAITING PARTY'}</Pill>
                  </div>
                  <div className="mt-[3px] font-mono text-[11px] text-[#6B7280]">
                    {reasonLabel(f.reason)} · against {f.againstSide || '—'} · {when(f.eventDate)}
                  </div>
                </div>
                <span className="flex-shrink-0 font-mono text-[13px] font-bold">{money(f.amountCents)}</span>
              </div>
              {f.freeText && <p className="mt-[10px] font-mono text-[13px] leading-[1.55] text-[#1A1A1A]">{f.freeText}</p>}
              {f.desiredOutcome && <div className="mt-1 font-mono text-[11px] text-[#6B7280]">Wants: {reasonLabel(f.desiredOutcome)}</div>}

              {canResolve && (
                <div className="mt-[14px] flex flex-col gap-[10px] border-t border-[#F2F2F2] pt-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-[10px] font-bold uppercase tracking-[0.1em] text-[#6B7280]">STRIKE</span>
                    {STRIKES.map((s) => (
                      <button
                        key={s.label}
                        type="button"
                        onClick={() => setStrikeById((m) => ({ ...m, [f.id]: s.key }))}
                        className={`${chip} ${strike === s.key ? 'bg-black text-white' : 'bg-[#F2F2F2] text-black hover:bg-black hover:text-white'}`}
                      >
                        {s.label}
                      </button>
                    ))}
                    <span className="ml-3 font-mono text-[10px] font-bold uppercase tracking-[0.1em] text-[#6B7280]">SPLIT $ TO BUYER</span>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={splitById[f.id] || ''}
                      onChange={(e) => setSplitById((m) => ({ ...m, [f.id]: e.target.value }))}
                      placeholder="0"
                      aria-label="Split dollars to buyer"
                      className="h-[34px] w-[84px] rounded-full border-2 border-transparent bg-[#F2F2F2] px-[14px] font-mono text-[11px] focus:border-black focus:bg-white focus:outline-none"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <button type="button" disabled={busy} onClick={() => resolve(f, 'released')} className={`${resolveBtn} bg-[#DCFCE7] text-[#15803D] hover:bg-[#15803D] hover:text-white`}>RELEASE TO PRO</button>
                    <button type="button" disabled={busy} onClick={() => resolve(f, 'refunded')} className={`${resolveBtn} bg-[#FEE2E2] text-[#DC2626] hover:bg-[#DC2626] hover:text-white`}>REFUND BUYER</button>
                    <button type="button" disabled={busy} onClick={() => resolve(f, 'split')} className={`${resolveBtn} bg-[#F2F2F2] text-black hover:bg-black hover:text-white`}>SPLIT</button>
                    <button type="button" disabled={busy} onClick={() => resolve(f, 'dismissed')} className={`${resolveBtn} bg-[#F7F7F7] text-[#6B7280] hover:bg-black hover:text-white`}>DISMISS</button>
                  </div>
                </div>
              )}
            </article>
          );
        })}
      </div>
    </>
  );
}
